import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Autocomplete,
  TextField,
  Divider,
  Link,
} from "@mui/material";
import { CloudUpload as UploadIcon } from "@mui/icons-material";
import { useNotify } from "react-admin";
import { getDocumentTypeConfig } from "@/constants/documentUpload";
import Button from "@/components/ui/Button";
import { ButtonIconLoadingPosition, UIComponentSize, ButtonStyleType } from "@/types/shared/uiComponents";
import { AlertType, FileAssignment, FileUploadStatus, TargetSchema, Vendor } from "@/types";
import { isFileTypeAccepted } from "@/utils/fileTypeDetection";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import {
  buildVendorRoleMeta,
  orderedPolicyVendorIds,
  resolvePrimaryTpaDefault,
  resolveRunoutNotice,
  vendorRoleTag,
  type VendorRoleMeta,
} from "./policyVendorSelector";
import {
  FileClientChoice,
  OTHER_VENDOR,
  VENDOR_TYPE_CONFIG,
  VendorAssignmentDialogProps,
  hasSheetSelection,
  needsSheetSelection,
} from "./vendorAssignmentShared";
import { useVendorOptions } from "./useVendorOptions";
import { AdministratorChoices } from "./AdministratorChoices";
import { FileRow } from "./FileRow";

// =============================================================================
// Panel
// =============================================================================

/**
 * Prepare-upload panel: per-file vendor assignment (radios from the policy, or a
 * search), per-file client, sheet picker and multi-client toggle, with the
 * footer actions. Rendered inline inside the upload dialog (#7551) or wrapped in
 * its own Dialog by {@link VendorAssignmentDialog} for standalone dropzones.
 */
export const VendorAssignmentPanel: React.FC<VendorAssignmentDialogProps> = ({
  open,
  files,
  targetSchema,
  onClose,
  onUpload,
  onRemoveFile,
  vendorType = "tpaRx",
  defaultVendorId,
  policyVendors,
  defaultClient,
  fileClients,
  onFileClientChange,
  onBack,
  onAddMore,
  addingMore,
  addMoreSlot,
  onDoneAdding,
  rowsVisible = true,
  onUploadingChange,
  fileClientEditable = true,
}) => {
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();

  const config = VENDOR_TYPE_CONFIG[vendorType];

  // Policy pinning/defaulting only applies to the TPA/Rx claims selector.
  const policyCtx = vendorType === "tpaRx" ? policyVendors : undefined;

  const [fileAssignments, setFileAssignments] = useState<FileAssignment[]>([]);
  const [bulkVendor, setBulkVendor] = useState<Vendor | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  useEffect(() => {
    onUploadingChange?.(isUploading);
  }, [isUploading, onUploadingChange]);
  // Files the user explicitly cleared — never re-stamped by the auto-fill effect, so a
  // background vendor-list refetch can't undo a deliberate clear (#5591, AC "no silent
  // auto-stamp"). Reset on bulk assign / upload / dialog close.
  const [userClearedIds, setUserClearedIds] = useState<Set<string>>(new Set());
  // Rows the user asked to change individually in the 3+ files compact layout
  // (#7552 AC6); also rows that picked "Different vendor…".
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [otherVendorRows, setOtherVendorRows] = useState<Set<string>>(new Set());

  const { data: filteredVendors = [], isLoading: loadingVendors } = useVendorOptions(vendorType, open, notifyError);

  // --- Policy vendor context (#5591 / #7552) ------------------------------
  const roleMeta = useMemo<Map<string, VendorRoleMeta>>(
    () => (policyCtx ? buildVendorRoleMeta(policyCtx) : new Map()),
    [policyCtx]
  );

  const pinnedIds = useMemo<Set<string>>(() => {
    if (!policyCtx) return new Set();
    const present = new Set(filteredVendors.map((v) => String(v.id)));
    return new Set(orderedPolicyVendorIds(policyCtx).filter((id) => present.has(id)));
  }, [policyCtx, filteredVendors]);

  // Policy vendors first (pin order), then the full A-Z list. MUI's groupBy needs
  // options pre-sorted by group or it repeats headers.
  const orderedVendors = useMemo<Vendor[]>(() => {
    if (!policyCtx || pinnedIds.size === 0) return filteredVendors;
    const byId = new Map(filteredVendors.map((v) => [String(v.id), v]));
    const pinned = orderedPolicyVendorIds(policyCtx)
      .map((id) => byId.get(id))
      .filter((v): v is Vendor => Boolean(v));
    const rest = filteredVendors.filter((v) => !pinnedIds.has(String(v.id)));
    return [...pinned, ...rest];
  }, [policyCtx, pinnedIds, filteredVendors]);

  /** The policy's administrators in pin order — the radio choices (#7552). */
  const policyChoices = useMemo<Vendor[]>(
    () => orderedVendors.filter((v) => pinnedIds.has(String(v.id))),
    [orderedVendors, pinnedIds]
  );
  // Radios are coming once the vendor list lands; hold the row steady until then.
  const choicesPending = loadingVendors && !!policyCtx && orderedPolicyVendorIds(policyCtx).length > 0;

  const clientFor = (fileId: string): FileClientChoice | null | undefined =>
    fileClients && fileId in fileClients ? fileClients[fileId] : defaultClient;
  /**
   * A row re-pointed at a different client than the batch's (#7551 AC8) must not
   * keep the batch client's administrators or primary TPA: that is the
   * confidently-wrong pre-fill #5591 exists to prevent (!4974 thread 31123). Such
   * rows get the search alone, no pre-fill, until a per-client context exists.
   */
  // A cleared client (explicit null, "Chosen at review") counts as foreign too: the
  // batch client's TPA must not ride along on a file that binds no client (31216).
  const hasForeignClient = (fileId: string): boolean => {
    if (!defaultClient) return false;
    const c = clientFor(fileId);
    return c === null || (!!c && String(c.id) !== String(defaultClient.id));
  };
  const foreignClientIds = Object.keys(fileClients ?? {})
    .filter(hasForeignClient)
    .join("|");

  // Softened default: the policy's single primary TPA (never the arbitrary first
  // vendor). An explicit defaultVendorId (e.g. the carrier path) still wins.
  const effectiveDefaultId = useMemo(
    () => defaultVendorId ?? (policyCtx ? resolvePrimaryTpaDefault(policyCtx) : undefined),
    [defaultVendorId, policyCtx]
  );

  const runoutCutover = useMemo(
    () => (policyCtx ? resolveRunoutNotice(policyCtx)?.cutoverDate : undefined),
    [policyCtx]
  );

  const groupBy =
    pinnedIds.size > 0
      ? (o: Vendor) => (pinnedIds.has(String(o.id)) ? "This policy's vendors" : "All vendors")
      : undefined;

  const renderVendorOption = (props: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key }, option: Vendor) => {
    const { key, ...rest } = props;
    const meta = roleMeta.get(String(option.id));
    const tag = meta ? vendorRoleTag(meta) : null;
    return (
      <li key={key} {...rest}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 1 }}>
          <Typography variant="body2">{option.company_name}</Typography>
          {tag && (
            <Typography
              variant="caption"
              sx={{ fontWeight: 500, flexShrink: 0, color: tag.isRunout ? "warning.main" : "primary.main" }}
            >
              {tag.label}
            </Typography>
          )}
        </Box>
      </li>
    );
  };

  // Filter valid files based on target schema
  useEffect(() => {
    const validFiles = files.filter((file) => {
      const docConfig =
        targetSchema === TargetSchema.STOPLOSS_CLAIM
          ? getDocumentTypeConfig("CLAIMS")
          : targetSchema === TargetSchema.REIMBURSEMENT
            ? getDocumentTypeConfig("REIMBURSEMENTS")
            : getDocumentTypeConfig("POLICIES");

      const acceptedTypes = docConfig ? docConfig.ACCEPTED_MIME_TYPES.slice() : [];
      return isFileTypeAccepted(file.file, acceptedTypes as string[]);
    });

    // Merge by id (!4974 thread 31124): a row the user already has keeps its
    // object — vendor pick, sheet picks, multi-client tick — and only new files
    // are appended, removed ones dropped. Replacing the array wiped every pick on
    // Add more files and let the auto-fill re-stamp the primary TPA.
    setFileAssignments((prev) => {
      const prevById = new Map(prev.map((a) => [a.id, a]));
      const next = validFiles.map((f) => prevById.get(f.id) ?? f);
      return next.length === prev.length && next.every((a, i) => a === prev[i]) ? prev : next;
    });

    const filteredCount = files.length - validFiles.length;
    if (filteredCount > 0) {
      notify(`${filteredCount} file(s) with invalid file types were excluded from upload.`, {
        type: AlertType.WARNING,
      });
    }
  }, [files, targetSchema, notify]);

  // Auto-assign the default vendor (explicit defaultVendorId, or the policy's single
  // primary TPA) to still-pending files once vendors are loaded. The checked radio
  // (#7552) is how the fill is shown rather than silent (#5591).
  useEffect(() => {
    if (!effectiveDefaultId || loadingVendors || filteredVendors.length === 0 || fileAssignments.length === 0) return;

    const defaultVendor = filteredVendors.find((v) => v.id === effectiveDefaultId);
    if (!defaultVendor) return;

    const pendingIds = fileAssignments
      .filter((f) => f.status === FileUploadStatus.PENDING && !userClearedIds.has(f.id) && !hasForeignClient(f.id))
      .map((f) => f.id);
    if (pendingIds.length === 0) return;

    setFileAssignments((prev) =>
      prev.map((assignment) =>
        pendingIds.includes(assignment.id)
          ? {
              ...assignment,
              carrierId: defaultVendor.id,
              carrierName: defaultVendor.company_name,
              status: FileUploadStatus.ASSIGNED,
            }
          : assignment
      )
    );
    // hasForeignClient is derived from fileClients; foreignClientIds is its value key.
  }, [effectiveDefaultId, filteredVendors, loadingVendors, fileAssignments.length, userClearedIds, foreignClientIds]);

  const handleVendorAssignment = (fileId: string, vendor: Vendor | null) => {
    if (!vendor) {
      // Remember the deliberate clear so the auto-fill effect won't re-stamp it.
      setUserClearedIds((prev) => new Set(prev).add(fileId));
      setFileAssignments((prev) =>
        prev.map((assignment) =>
          assignment.id === fileId
            ? {
                ...assignment,
                carrierId: undefined,
                carrierName: undefined,
                status: FileUploadStatus.PENDING,
              }
            : assignment
        )
      );
      return;
    }

    setFileAssignments((prev) =>
      prev.map((assignment) =>
        assignment.id === fileId
          ? {
              ...assignment,
              carrierId: vendor.id,
              carrierName: vendor.company_name,
              status: FileUploadStatus.ASSIGNED,
            }
          : assignment
      )
    );
  };

  /** Radio change on one row (#7552): a policy vendor assigns; "Different vendor…" clears and reveals the search. */
  const handleChoiceChange = (fileId: string, value: string) => {
    if (value === OTHER_VENDOR) {
      setOtherVendorRows((prev) => new Set(prev).add(fileId));
      handleVendorAssignment(fileId, null);
      return;
    }
    setOtherVendorRows((prev) => {
      if (!prev.has(fileId)) return prev;
      const next = new Set(prev);
      next.delete(fileId);
      return next;
    });
    const vendor = orderedVendors.find((v) => String(v.id) === value) ?? null;
    handleVendorAssignment(fileId, vendor);
  };

  const assignVendorToAll = (vendor: Vendor, opts: { skipExpanded?: boolean } = {}) => {
    setFileAssignments((prev) =>
      prev.map((assignment) =>
        opts.skipExpanded && expandedRows.has(assignment.id)
          ? assignment
          : {
              ...assignment,
              carrierId: vendor.id,
              carrierName: vendor.company_name,
              status: FileUploadStatus.ASSIGNED,
            }
      )
    );
    // Bulk assign re-assigns every file, incl. cleared ones — except the rows the
    // user changed individually when skipExpanded is set: their cleared / "Different
    // vendor…" state must survive, or the auto-fill re-stamps the primary on them
    // (#7552 AC6/AC7; !4974 thread 31125).
    const keep = (prev: Set<string>) =>
      opts.skipExpanded ? new Set([...prev].filter((id) => expandedRows.has(id))) : new Set<string>();
    setUserClearedIds(keep);
    setOtherVendorRows(keep);
  };

  const handleBulkAssignment = () => {
    if (!bulkVendor) {
      notify(`Please select a ${config.label} for bulk assignment`, {
        type: AlertType.WARNING,
      });
      return;
    }
    const vendorName = bulkVendor.company_name;
    assignVendorToAll(bulkVendor);
    setBulkVendor(null);
    notify(`All files assigned to ${vendorName}`, { type: AlertType.SUCCESS });
  };

  /** Toolbar radio group for 3+ files (#7552 AC6): rows changed individually keep their pick. */
  const handleAssignAllChoice = (value: string) => {
    const vendor = policyChoices.find((v) => String(v.id) === value);
    if (vendor) assignVendorToAll(vendor, { skipExpanded: true });
  };

  /** A row leaving the batch client drops a batch-policy pre-fill; returning to it lets the default apply again. */
  const handleRowClientChange = (fileId: string, client: FileClientChoice | null) => {
    onFileClientChange?.(fileId, client);
    const foreign = !!defaultClient && (client === null || String(client.id) !== String(defaultClient.id));
    const row = fileAssignments.find((a) => a.id === fileId);
    // A pre-fill is a batch-policy administrator or the batch default (the carrier
    // path's defaultVendorId) — both came from the batch client, neither belongs on
    // a file that binds another client or none.
    const prefilled =
      !!row?.carrierId && (policyChoices.some((v) => v.id === row.carrierId) || row.carrierId === effectiveDefaultId);
    if (foreign && prefilled) {
      setFileAssignments((prev) =>
        prev.map((a) =>
          a.id === fileId ? { ...a, carrierId: undefined, carrierName: undefined, status: FileUploadStatus.PENDING } : a
        )
      );
      setUserClearedIds((prev) => new Set(prev).add(fileId));
    } else if (!foreign) {
      setUserClearedIds((prev) => {
        if (!prev.has(fileId)) return prev;
        const next = new Set(prev);
        next.delete(fileId);
        return next;
      });
    }
  };

  const handleRemoveFile = (fileId: string) => {
    setFileAssignments((prev) => prev.filter((a) => a.id !== fileId));
    onRemoveFile(fileId);
  };

  const handleMultiClientChange = (fileId: string, checked: boolean) => {
    setFileAssignments((prev) => prev.map((a) => (a.id === fileId ? { ...a, is_multi_client: checked } : a)));
    // A book-of-business file binds no single client (ADR-057); it goes through the
    // same path as a row-level clear so a batch pre-fill is dropped with the client
    // (!4974 thread 31216 round 3).
    if (checked) handleRowClientChange(fileId, null);
  };

  const handleSheetSelectionChange = (fileId: string, sheetName: string, checked: boolean) => {
    setFileAssignments((prev) =>
      prev.map((a) => {
        if (a.id !== fileId) return a;
        const next = new Set(a.selectedSheets ?? []);
        if (checked) next.add(sheetName);
        else next.delete(sheetName);
        // Preserve the workbook's sheet order so the MSW-5 per-sheet upload
        // sequence matches the user's mental model reading the picker top-to-bottom.
        const ordered = (a.sheetNames ?? []).filter((s) => next.has(s));
        return { ...a, selectedSheets: ordered };
      })
    );
  };

  const handleSelectAllSheets = (fileId: string, checked: boolean) => {
    setFileAssignments((prev) =>
      prev.map((a) => {
        if (a.id !== fileId) return a;
        return { ...a, selectedSheets: checked ? [...(a.sheetNames ?? [])] : [] };
      })
    );
  };

  const assignedCount = fileAssignments.filter((f) => f.status === FileUploadStatus.ASSIGNED).length;
  const allFilesAssigned = fileAssignments.length > 0 && assignedCount === fileAssignments.length;
  const unassignedCount = fileAssignments.length - assignedCount;
  const allFilesReady =
    allFilesAssigned && fileAssignments.every((f) => !needsSheetSelection(f) || hasSheetSelection(f));

  const resetLocalState = () => {
    setBulkVendor(null);
    setUserClearedIds(new Set());
    setExpandedRows(new Set());
    setOtherVendorRows(new Set());
  };

  const handleUploadAll = async () => {
    if (!allFilesReady) {
      if (!allFilesAssigned) {
        notify(`Please assign ${config.labelPlural} to all files before uploading`, {
          type: AlertType.WARNING,
        });
      } else {
        notify("Please select at least one sheet for each multi-sheet file", {
          type: AlertType.WARNING,
        });
      }
      return;
    }

    setIsUploading(true);

    try {
      await onUpload(fileAssignments);
      setFileAssignments([]);
      resetLocalState();
    } catch (error) {
      notifyError(error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCloseDialog = () => {
    if (isUploading) return;
    // Clear per-file assignments (incl. the multi-client toggle) so reopening
    // with the same file list doesn't carry stale selections.
    setFileAssignments([]);
    resetLocalState();
    onClose();
  };

  // 3+ files with a policy context: one radio group in the toolbar, compact rows (#7552 AC6).
  const compactRows = fileAssignments.length >= 3 && policyChoices.length > 0;
  // Every row shares the same vendor → the toolbar group can show it as checked.
  const sharedVendorId = (() => {
    const ids = new Set(fileAssignments.map((f) => f.carrierId ?? ""));
    return ids.size === 1 ? [...ids][0] : "";
  })();

  return (
    <>
      {/* Toolbar: assignment count on the left; Assign all (3+ files) on the right. */}
      {rowsVisible && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
            mb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="body2" color="text.secondary">
              {assignedCount} of {fileAssignments.length} files assigned
            </Typography>
            {unassignedCount > 0 && (
              <Chip label={`${unassignedCount} unassigned`} size="small" color="warning" variant="outlined" />
            )}
          </Box>

          {compactRows && (
            <AdministratorChoices
              name="assign-all-administrator"
              ariaLabel="Administrator for all files"
              title="Assign all"
              choices={policyChoices}
              roleMeta={roleMeta}
              runoutCutover={runoutCutover}
              value={sharedVendorId}
              disabled={loadingVendors || isUploading}
              onChange={handleAssignAllChoice}
              dense
              otherHelper={`Search all ${config.labelPlural}.`}
            />
          )}

          {!compactRows && fileAssignments.length >= 3 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Autocomplete
                size="small"
                sx={{ width: 260 }}
                options={orderedVendors}
                groupBy={groupBy}
                getOptionLabel={(option) => option.company_name || ""}
                value={bulkVendor}
                onChange={(_e, newValue) => setBulkVendor(newValue)}
                disabled={loadingVendors || isUploading}
                loading={loadingVendors}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={`Select ${config.label}...`}
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingVendors && <CircularProgress color="inherit" size={18} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={renderVendorOption}
              />
              <Button
                variant={ButtonStyleType.Contained}
                onClick={handleBulkAssignment}
                disabled={!bulkVendor || loadingVendors || isUploading}
                size={UIComponentSize.Small}
              >
                Assign All
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* File List */}
      {rowsVisible && (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
          {fileAssignments.map((assignment, index) => (
            <React.Fragment key={assignment.id}>
              {index > 0 && <Divider />}
              <FileRow
                assignment={assignment}
                vendors={orderedVendors}
                groupBy={groupBy}
                renderVendorOption={renderVendorOption}
                policyChoices={hasForeignClient(assignment.id) ? [] : policyChoices}
                roleMeta={roleMeta}
                runoutCutover={runoutCutover}
                otherVendorMode={otherVendorRows.has(assignment.id)}
                compact={compactRows && !expandedRows.has(assignment.id) && !hasForeignClient(assignment.id)}
                onExpand={() => setExpandedRows((prev) => new Set(prev).add(assignment.id))}
                config={config}
                loadingVendors={loadingVendors}
                choicesPending={choicesPending && !hasForeignClient(assignment.id)}
                isUploading={isUploading}
                onAssign={handleVendorAssignment}
                onChoiceChange={handleChoiceChange}
                onRemove={handleRemoveFile}
                onSheetChange={handleSheetSelectionChange}
                onSelectAllSheets={handleSelectAllSheets}
                multiClientEligible={targetSchema === TargetSchema.REIMBURSEMENT}
                onMultiClientChange={handleMultiClientChange}
                client={clientFor(assignment.id)}
                clientEditable={!!onFileClientChange && fileClientEditable}
                onClientChange={(c) => handleRowClientChange(assignment.id, c)}
              />
            </React.Fragment>
          ))}

          {fileAssignments.length === 0 && (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No files to assign
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Add more files: the drop target opens HERE, between the rows and the
          footer, so Back / Cancel / Upload stay the batch's controls below it. */}
      {rowsVisible && onAddMore && !addingMore && (
        <Box sx={{ mt: 1.5 }}>
          <Link
            component="button"
            type="button"
            onClick={onAddMore}
            underline="hover"
            variant="body2"
            sx={{ fontWeight: 600, fontSize: 13 }}
          >
            + Add more files
          </Link>
        </Box>
      )}
      {/* The slot is ALWAYS mounted (the parent hides it with display:none when
          not adding): the drop target inside holds FilePond's items, and an
          unmount would re-issue their ids and reset the rows. */}
      {addMoreSlot && (
        <Box sx={{ mt: rowsVisible && addingMore ? 1.5 : 0 }}>
          {addMoreSlot}
          {rowsVisible && addingMore && onDoneAdding && (
            <Box sx={{ mt: 0.75, textAlign: "right" }}>
              <Link
                component="button"
                type="button"
                onClick={onDoneAdding}
                underline="hover"
                variant="body2"
                sx={{ fontWeight: 600, fontSize: 13 }}
              >
                Done adding files
              </Link>
            </Box>
          )}
        </Box>
      )}

      {rowsVisible && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: onBack ? "space-between" : "flex-end",
            gap: 1,
            pt: 2.5,
          }}
        >
          {onBack && (
            <Button variant={ButtonStyleType.Text} onClick={onBack} disabled={isUploading}>
              Back
            </Button>
          )}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant={ButtonStyleType.Text} onClick={handleCloseDialog} disabled={isUploading}>
              Cancel
            </Button>
            <Button
              variant={ButtonStyleType.Contained}
              startIcon={<UploadIcon />}
              loading={isUploading}
              loadingPosition={ButtonIconLoadingPosition.Start}
              onClick={handleUploadAll}
              disabled={!allFilesReady || isUploading || fileAssignments.length === 0}
            >
              {isUploading
                ? "Uploading..."
                : `Upload ${fileAssignments.length} File${fileAssignments.length !== 1 ? "s" : ""}`}
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
};

/**
 * Standalone Dialog around {@link VendorAssignmentPanel}, for dropzones that live
 * on a page (policy / claim / reimbursement create) rather than inside the upload
 * dialog. The upload dialog renders the panel inline instead (#7551).
 */
export const VendorAssignmentDialog: React.FC<VendorAssignmentDialogProps> = (props) => {
  const config = VENDOR_TYPE_CONFIG[props.vendorType ?? "tpaRx"];
  return (
    <Dialog open={props.open} onClose={props.onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {/* component="span": DialogTitle already renders an <h2>; an inner <h3>
            would be an invalid nested heading (hydration/a11y warning). Keep the
            h3 styling, drop the heading semantics. */}
        <Typography variant="h3" component="span">
          Prepare upload — {config.labelPlural}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <VendorAssignmentPanel {...props} onBack={undefined} onAddMore={undefined} />
      </DialogContent>
    </Dialog>
  );
};

// Re-exports: the pieces moved to their own files in the !4974 split; existing
// importers (DocumentDropzone, index.ts, tests) keep the same entry point.
export { ClientSearchField } from "./ClientSearchField";
export { useVendorOptions } from "./useVendorOptions";
export { AdministratorChoices } from "./AdministratorChoices";
export {
  hasSheetSelection,
  needsSheetSelection,
  type FileClientChoice,
  type VendorAssignmentDialogProps,
  type VendorAssignmentType,
} from "./vendorAssignmentShared";
